import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["ref"],
    _excluded2 = ["theme", "size", "onFocus", "onBlur", "value", "onChange", "invalid", "success", "readOnly", "disabled", "label", "placeholder", "type", "maxLength", "autoFocus", "innerProps", "transformOnBlur", "mask", "iconLeft", "iconLeftProps", "iconRight", "iconRightProps", "inputProps", "hideEye", "tooltip", "tooltipAbsolute", "tooltipProps", "subText", "classes", "name", "id", "autoComplete", "namespace", "subTextProps"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { forwardRef, useCallback, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { defaultPropObject } from "../constants";
import { useCombinedRefs } from "../hooks";
import { CheckLinear, ErrorCircleFilled, EyeClosed, EyeOpened } from "../icons";
import { FieldIcon, FieldInput, FieldRoot, useField } from "../internal/Field"; // eslint-disable-next-line prettier/prettier

import Loader from '../Loader';
import styles from './Textfield.css';
var cx = classNames.bind(styles);
var defaultInnerProps = {};
var defaultInputProps = {};

var isEyeButton = function isEyeButton(target) {
  return target && target.dataset && target.dataset.id === 'password-eye';
};

var handleMouseDown = function handleMouseDown(e) {
  if (isEyeButton(e.currentTarget)) {
    e.preventDefault();
  }
};

var Textfield = /*#__PURE__*/forwardRef(function Textfield(_ref, ref) {
  var _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? 'b2c' : _ref$theme,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'medium' : _ref$size,
      onFocus = _ref.onFocus,
      onBlur = _ref.onBlur,
      value = _ref.value,
      onChange = _ref.onChange,
      invalid = _ref.invalid,
      success = _ref.success,
      readOnly = _ref.readOnly,
      disabled = _ref.disabled,
      label = _ref.label,
      placeholder = _ref.placeholder,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'text' : _ref$type,
      maxLength = _ref.maxLength,
      autoFocus = _ref.autoFocus,
      _ref$innerProps = _ref.innerProps;
  _ref$innerProps = _ref$innerProps === void 0 ? defaultInnerProps : _ref$innerProps;
  var isPopupOpen = _ref$innerProps.isPopupOpen,
      transformOnBlur = _ref.transformOnBlur,
      mask = _ref.mask,
      iconLeft = _ref.iconLeft,
      _ref$iconLeftProps = _ref.iconLeftProps,
      iconLeftProps = _ref$iconLeftProps === void 0 ? defaultPropObject : _ref$iconLeftProps,
      iconRight = _ref.iconRight,
      iconRightProps = _ref.iconRightProps,
      _ref$inputProps = _ref.inputProps;
  _ref$inputProps = _ref$inputProps === void 0 ? defaultInputProps : _ref$inputProps;

  var inputPropsRef = _ref$inputProps.ref,
      inputProps = _objectWithoutProperties(_ref$inputProps, _excluded),
      hideEye = _ref.hideEye,
      tooltip = _ref.tooltip,
      tooltipAbsolute = _ref.tooltipAbsolute,
      _ref$tooltipProps = _ref.tooltipProps,
      tooltipProps = _ref$tooltipProps === void 0 ? defaultPropObject : _ref$tooltipProps,
      subText = _ref.subText,
      _ref$classes = _ref.classes,
      classes = _ref$classes === void 0 ? defaultPropObject : _ref$classes,
      name = _ref.name,
      id = _ref.id,
      autoComplete = _ref.autoComplete,
      namespace = _ref.namespace,
      _ref$subTextProps = _ref.subTextProps,
      subTextProps = _ref$subTextProps === void 0 ? defaultPropObject : _ref$subTextProps,
      props = _objectWithoutProperties(_ref, _excluded2);

  var inputRef = useRef(null);
  var combinedRef = useCombinedRefs(inputRef, inputPropsRef);

  var _useField = useField({
    onBlur: onBlur,
    onFocus: onFocus,
    namespace: namespace,
    ref: inputRef,
    componentName: 'textField'
  }),
      focused = _useField.focused,
      handleBlurHook = _useField.handleBlur,
      handleFocus = _useField.handleFocus,
      getTestId = _useField.getTestId,
      isAutofill = _useField.isAutofill;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      passwordVisible = _useState2[0],
      setPasswordVisible = _useState2[1];

  var hasTransformOnBlur = transformOnBlur && !focused;
  var isPassword = type === 'password';
  var fieldInputValue = transformOnBlur && !focused ? transformOnBlur(value) : value;

  var handleBlur = function handleBlur(e) {
    var relatedTarget = e.relatedTarget;

    if (isEyeButton(relatedTarget)) {
      e.relatedTarget.focus();
      return;
    }

    handleBlurHook(e);
  };

  var handleTogglePasswordVisible = useCallback(function () {
    setPasswordVisible(!passwordVisible);
    setTimeout(function () {
      if (inputRef.current) {
        inputRef.current.selectionStart = inputRef.current.value.length;
        inputRef.current.selectionEnd = inputRef.current.value.length;
      }
    }, 0);
  }, [inputRef, passwordVisible]);

  var getType = function getType() {
    if (isPassword && passwordVisible) {
      return 'text';
    }

    return type;
  };

  return /*#__PURE__*/React.createElement(FieldRoot, _extends({}, props, {
    ref: ref,
    readOnly: readOnly,
    disabled: disabled,
    focused: focused,
    invalid: invalid,
    size: size,
    subText: subText,
    success: success,
    theme: theme,
    tooltip: tooltip,
    tooltipAbsolute: tooltipAbsolute,
    tooltipProps: tooltipProps,
    autofill: isAutofill,
    classes: {
      root: cx('root', classes.root),
      container: cx({
        popup_open: isPopupOpen
      }, classes.container)
    },
    subTextProps: _objectSpread({
      errorIcon: /*#__PURE__*/React.createElement(ErrorCircleFilled, {
        size: "md"
      })
    }, subTextProps)
  }, getTestId()), iconLeft && /*#__PURE__*/React.createElement(FieldIcon, _extends({
    position: "left"
  }, iconLeftProps, {
    classes: {
      root: cx('icon', classes.icon)
    },
    loader: /*#__PURE__*/React.createElement(Loader, {
      size: "small"
    })
  }), iconLeft), /*#__PURE__*/React.createElement(FieldInput, _extends({}, inputProps, getTestId('input'), {
    classes: {
      root: classes.inner,
      input: classes.input,
      label: classes.label
    },
    onFocus: handleFocus,
    onBlur: handleBlur,
    onChange: onChange,
    readOnly: readOnly,
    disabled: disabled,
    placeholder: placeholder,
    type: getType(),
    value: fieldInputValue,
    mask: hasTransformOnBlur ? '' : mask,
    maxLength: maxLength,
    autoFocus: autoFocus,
    ref: combinedRef,
    name: name,
    autoComplete: autoComplete,
    id: id,
    invalid: invalid,
    theme: theme,
    label: label
  })), iconRight && /*#__PURE__*/React.createElement(FieldIcon, _extends({
    invalid: invalid,
    theme: theme,
    position: "right"
  }, iconRightProps, {
    classes: {
      root: cx('icon', classes.icon)
    },
    loader: /*#__PURE__*/React.createElement(Loader, {
      size: "small"
    })
  }), iconRight), isPassword && !hideEye && !iconRight && /*#__PURE__*/React.createElement(FieldIcon, _extends({
    invalid: invalid,
    theme: theme,
    classes: {
      root: cx('button_eye', 'icon', {
        button_eye_show: focused
      }, classes.icon)
    },
    "data-id": "password-eye",
    onClick: handleTogglePasswordVisible,
    onMouseDown: handleMouseDown,
    position: "right",
    loader: /*#__PURE__*/React.createElement(Loader, {
      size: "small"
    })
  }, getTestId('passwordEye')), passwordVisible ? /*#__PURE__*/React.createElement(EyeOpened, {
    size: "md"
  }) : /*#__PURE__*/React.createElement(EyeClosed, {
    size: "md"
  })), success && !iconRight && /*#__PURE__*/React.createElement(FieldIcon, {
    invalid: invalid,
    theme: theme,
    classes: {
      root: cx('success', 'icon', {
        success_show: hideEye || !(isPassword && focused)
      }, classes.icon)
    },
    position: "right",
    loader: /*#__PURE__*/React.createElement(Loader, {
      size: "small"
    })
  }, /*#__PURE__*/React.createElement(CheckLinear, {
    size: "md"
  })));
});
export default Textfield;